import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { homeInventoryInfoService } from '../../../../services/homeInventoryInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('HomeInventoryData');

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

export const HomeInventoryDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  const [homeInventoryData, setHomeInventoryData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSerialNumber, setShowSerialNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadHomeInventoryData();
  }, []);

  const loadHomeInventoryData = async () => {
    try {
      setLoading(true);
      const data = await homeInventoryInfoService.getAllHomeInventoryInfo(targetUserId);

      if (data && data.length > 0) {
        // Load serial number data and photos for each home inventory item
        const updatedData = await Promise.all(
          data.map(async (item) => {
            // Load serial number from vault
            try {
              logger.debug('[Serial Debug] Attempting to load serial number for home inventory ID:', item.id);
              const serialData = await vaultService.getHomeInventoryData(item.id, targetUserId);
              logger.debug('[Serial Debug] Serial data retrieved for home inventory:', item.id, serialData);
              if (serialData && serialData.serialNumber) {
                item.serialNumber = serialData.serialNumber;
                logger.debug('[Serial Debug] Assigned serial number to home inventory data');
              } else {
                logger.debug('[Serial Debug] No serial number data returned for home inventory:', item.id);
              }
            } catch (vaultError) {
              logger.debug('[Serial Debug] Error loading serial number for home inventory:', item.id, vaultError);
            }

            // Load photos from S3
            try {
              const photos = await homeInventoryInfoService.getPhotos(item.id);
              if (photos && photos.length > 0) {
                item.photos = photos;
                logger.debug('[Photo Debug] Loaded photos for item:', item.id, photos.length);
              }
            } catch (photoError) {
              logger.debug('[Photo Debug] Error loading photos for home inventory:', item.id, photoError);
            }

            return item;
          })
        );

        logger.debug('[Data Debug] Final home inventory data being set:', updatedData.length, 'records');
        setHomeInventoryData(updatedData);
      } else {
        // Set empty array when no data
        logger.debug('[Data Debug] No home inventory data, setting empty array');
        setHomeInventoryData([]);
      }
    } catch (error) {
      logger.error('Error loading home inventory data:', error);
      Alert.alert('Error', 'Failed to load home inventory information');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleEditHomeInventory = (item: any) => {
    navigation.navigate('HomeInventoryInput', { 
      homeInventoryId: item.id, 
      isEdit: true 
    });
  };

  const handleDeleteHomeInventory = async (item: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this home inventory item?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await homeInventoryInfoService.deleteHomeInventoryInfo(item.id);
            
            // Reload data after deletion
            await loadHomeInventoryData();
            
            Alert.alert('Deleted', 'Home inventory item deleted.');
          } catch (e) {
            logger.error('Delete home inventory failed:', e);
            Alert.alert('Error', 'Failed to delete home inventory item');
          }
        } 
      },
    ]);
  };

  const handleAddHomeInventory = () => {
    navigation.navigate('HomeInventoryInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Inventory Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading home inventory information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!homeInventoryData || homeInventoryData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Home Inventory Information</Text>

        <View style={styles.whiteContainer}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No home inventory items found</Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddHomeInventory}
              >
                <Text style={styles.addButtonText}>Add Home Inventory Item</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Home Inventory Information</Text>

      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {homeInventoryData.map((item, index) => (
            <View key={item.id || index} style={styles.itemCard}>
              <View style={styles.itemHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.itemName}>
                    {item.itemName || 'Unnamed Item'}
                  </Text>
                  <Text style={styles.itemCategory}>
                    {item.category ? `Category: ${item.category}` : 'No category'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editItemButton}
                      onPress={() => handleEditHomeInventory(item)}
                    >
                      <Text style={styles.editItemButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteItemButton}
                      onPress={() => handleDeleteHomeInventory(item)}
                    >
                      <Text style={styles.deleteItemButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Item Name" value={item.itemName || ''} />
              <InfoItem label="Category" value={item.category || ''} />
              <InfoItem label="Brand" value={item.brand || ''} />
              <InfoItem label="Model" value={item.model || ''} />
              
              {item.serialNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Serial Number</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showSerialNumber[item.id] ? item.serialNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowSerialNumber(prev => ({ ...prev, [item.id]: !prev[item.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showSerialNumber[item.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Purchase Date" value={item.purchaseDate || ''} />
              <InfoItem label="Purchase Price" value={item.purchasePrice ? formatCurrency(item.purchasePrice) : ''} />
              <InfoItem label="Current Value" value={item.currentValue ? formatCurrency(item.currentValue) : ''} />
              <InfoItem label="Condition" value={item.condition || ''} />
              <InfoItem label="Location in Home" value={item.locationInHome || ''} />
              <InfoItem label="Warranty Expiration" value={item.warrantyExpiration || ''} />
              <InfoItem label="Notes" value={item.notes || ''} />

              {/* Photos Section */}
              {item.photos && item.photos.length > 0 && (
                <View style={styles.photosSection}>
                  <Text style={styles.label}>Photos ({item.photos.length})</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photosScroll}>
                    {item.photos.map((photo: { key: string; url: string }, photoIndex: number) => (
                      <Image
                        key={photo.key || photoIndex}
                        source={{ uri: photo.url }}
                        style={styles.photoThumbnail}
                        resizeMode="cover"
                      />
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>
          ))}


          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherItemButton}
                onPress={handleAddHomeInventory}
              >
                <Text style={styles.addAnotherItemButtonText}>Add Another Item</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PropertyChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  itemCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  itemName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  itemCategory: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editItemButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editItemButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteItemButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteItemButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherItemButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherItemButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  photosSection: {
    marginTop: ms(10),
    marginBottom: ms(15),
  },
  photosScroll: {
    marginTop: ms(8),
  },
  photoThumbnail: {
    width: s(100),
    height: s(100),
    borderRadius: ms(10),
    marginRight: ms(10),
    backgroundColor: '#E5E5E5',
  },
});